import { isAcCable } from "./ac";
import type { CableSpec } from "./cable-current";
import type { DefLookup } from "./catalog";
import { effectiveTerminals } from "./effective";
import { isSenseTerminal } from "./sense-terminal";
import type { Cable, ComponentDefinition, SystemNode, Terminal } from "./types";

/** A battery is checked against its bank limit; a load/source lug often just
 * repeats its own demand, and the junction glyph has no physical rating. */
export function isRatedPassThrough(def: ComponentDefinition): boolean {
  return def.category !== "battery" && def.renderVariant !== "junction" &&
    def.ampDraw == null && def.ampOutput == null;
}

/** Only series contacts on this conductor constrain its inline fuse. A shared
 * bus stud is checked on the current it carries; a branch's rating must not
 * constrain another branch or the feeder. */
export function seriesFuseTerminals(
  cable: Cable, nodes: Record<string, SystemNode>, lookup: DefLookup, isCranking: boolean,
): { nodeId: string; terminal: Terminal }[] {
  if (isCranking || isAcCable(cable)) return [];
  const ends = [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ];
  const rated: { nodeId: string; terminal: Terminal }[] = [];
  for (const [nodeId, terminalId] of ends) {
    const node = nodes[nodeId];
    if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def || !isRatedPassThrough(def)) continue;
    const terminal = effectiveTerminals(def, node).find(t => t.id === terminalId);
    if (terminal && terminal.maxAmp > 0 &&
        (terminal.direction === "input" || terminal.direction === "output") &&
        !isSenseTerminal(def, terminal)) rated.push({ nodeId, terminal });
  }
  return rated;
}

export function fuseExceedsTerminalLimit(
  spec: Pick<CableSpec, "terminalFuseLimitA"> | null | undefined, rating: number | null | undefined,
): boolean {
  return spec?.terminalFuseLimitA != null && rating != null && rating > spec.terminalFuseLimitA;
}

/** A battery-post fuse mirrored onto the cable is still one trunk device,
 * distinct from a local automatic branch fuse. */
export function autoFuseNeedsReview(
  cable: Pick<Cable, "fuseAmpOverride" | "fuseAmpAuto">, spec: CableSpec | null | undefined,
): boolean {
  return cable.fuseAmpAuto === true && !spec?.protectionIsBatteryPost &&
    fuseExceedsTerminalLimit(spec, cable.fuseAmpOverride);
}
